import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  TextField,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
} from '@mui/material';
import {
  Description as DocumentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { documentsApi } from '../../services/api';
import { DocumentItem } from '../../types';
import { extractApiError } from '../../apiUnwrapper/apiUnwrapper';
import { useSnackbar } from 'notistack';
import {
  PageHeader,
} from '../../components/SharedPageParts';

const DocumentsUploadPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

    const [mineName, setMineName] = useState('');
    const [department, setDepartment] = useState('');
    const [category, setCategory] = useState('');
    const [description, setDescription] = useState('');
    const [file, setFile] = useState<File | null>(null);

    const [documents, setDocuments] = useState<DocumentItem[]>([]);

    const handleUpload = async () => {
      if (!file) {
        enqueueSnackbar("Please select a file before uploading.", { variant: "warning" });
        return;
      }

      setIsUploading(true);
      setProgress(10);

      const formData = new FormData();
      formData.append("mineName", mineName);
      formData.append("department", department);
      formData.append("category", category);
      formData.append("description", description);
      formData.append("file", file);

      try {
        setProgress(40);

        const response = await documentsApi.uploadDocument(formData);
        const result = response.data; // your ApiResponse<T>.data

        setProgress(70);

        // --- Handle backend success/failure (both return 200) ---
        if (!result || result.fileId === null || result.status !== "File Uploaded") {
          const errorMessage = result?.message || "Upload failed";
          enqueueSnackbar(`Upload failed: ${errorMessage}`, { variant: "error" });
          setUploadError(errorMessage);
          setSuccess(null);
          setIsUploading(false);
          return;
        }

        // --- Success case ---
        enqueueSnackbar("Document uploaded successfully!", { variant: "success" });
        setSuccess(`Document uploaded successfully! File ID: ${result.fileId}`);
        setUploadError(null);

        setProgress(100);

        // Reload list
        await loadDocuments();

      } catch (error) {
        // This only triggers on network errors or unexpected exceptions
        const apiError = extractApiError(error);
        enqueueSnackbar(`Upload failed: ${apiError.message}`, { variant: "error" });
        setUploadError(apiError.message);
        setSuccess(null);
      } finally {
        setIsUploading(false);
      }
    };

   useEffect(() => {
      loadDocuments();
    }, []);

    const loadDocuments = async () => {
        try {
            const response = await documentsApi.listDocuments();
            setDocuments(response);
        }catch (error) {
            const apiError = extractApiError(error);
            enqueueSnackbar(`Failed to load documents: ${apiError.message}`, { variant: "error" });
        }
    };

    // -----------------------------
  // Status Badge Component
  // -----------------------------
    const getColor = (line: string) => {
      if (line.includes("Failed")) return "red"
      return "green";
    };

    const StatusBadge = ({ status }: { status: string }) => {
      const lines = status.split("\n");

      return (
        <div>
          {lines.map((line, idx) => (
            <div key={idx} style={{ color: getColor(line) }}>
              {line}
            </div>
          ))}
        </div>
      );
    };

    const formatFileSize = (bytes: number): string => {
      if (bytes < 1024) {
        return `${bytes} bytes`;
      }

      const kb = bytes / 1024;
      if (kb < 1024) {
        return `${kb.toFixed(2)} KB`;
      }

      const mb = kb / 1024;
      if (mb < 1024) {
        return `${mb.toFixed(2)} MB`;
      }

      const gb = mb / 1024;
      return `${gb.toFixed(2)} GB`;
    };


    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      {/* Main Content */}
      <Box className="MuiBox-root-margin" sx={{ px: 1, mx: 'auto' }}>
        <PageHeader
                  title="Documents Portal"
                  Icon={DocumentIcon}
                  onBack={() => navigate('/')}
                  backLabel="Back to Dashboard"
                  subtitle=""
                />
      {uploadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {uploadError}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {isUploading && (
              <Box sx={{ width: '100%', mb: 2 }}>
                <LinearProgress variant="determinate" value={progress} />
              </Box>
            )}
      <Card sx={{ mb: 1, backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
        <CardContent>
            <Box
                component="form"
                sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
                mb: 3,
                }}
            >
                {/* Mine Name */}
                <TextField
                label="Mine Name"
                variant="outlined"
                fullWidth
                value={mineName}
                onChange={(e) => setMineName(e.target.value)}
                />

                {/* Department */}
                <TextField
                label="Department"
                variant="outlined"
                fullWidth
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                />

                {/* Document Category */}
                <FormControl fullWidth>
                <InputLabel>Document Category</InputLabel>
                <Select
                    value={category}
                    label="Document Category"
                    onChange={(e) => setCategory(e.target.value)}
                >
                    <MenuItem value="Safety">Safety</MenuItem>
                    <MenuItem value="Operations">Operations</MenuItem>
                    <MenuItem value="Maintenance">Maintenance</MenuItem>
                </Select>
                </FormControl>

                {/* Description (full width) */}
                <TextField
                    label="Description"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={2}
                    sx={{ gridColumn: '1 / -1' }}
                    value={description}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => 
                        setDescription(e.target.value)
                    }
                    />
                {/* File Upload */}
                <Box sx={{ gridColumn: '1 / -1' }}>
                <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{ textAlign: 'left' }}
                >
                    {file ? file.name : 'Choose File'}
                    <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.dwg,.dxf"
                    onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setFile(file);
                    }}

                    />
                </Button>
                </Box>

                {/* Upload Button */}
                <Box sx={{ gridColumn: '1 / -1', textAlign: 'right' }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpload}
                    disabled={!file}
                >
                    Upload Document
                </Button>
                </Box>
            </Box>
        </CardContent>
      </Card>
        <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
        <CardContent>    
            <TableContainer component={Paper} variant="outlined" sx={{ backgroundColor: theme.palette.background.default, borderColor: theme.palette.divider }}>
                <Table>
                <TableHead>
                    <TableRow>
                    <TableCell>Mine</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell sx = {{ width: '20%' }}>Description</TableCell>
                    <TableCell>File Name</TableCell>
                    <TableCell>File Size</TableCell>
                    <TableCell sx = {{ width: '30%' }}>Status</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.fileId}>
                      <TableCell>{doc.mineName}</TableCell>
                      <TableCell>{doc.department}</TableCell>
                      <TableCell>{doc.category}</TableCell>
                      <TableCell>{doc.description}</TableCell>
                      <TableCell>{doc.originalFilename}</TableCell>
                      <TableCell>{formatFileSize(doc.fileSizeBytes)}</TableCell>
                      <TableCell>
                        <StatusBadge status={doc.statusComment} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                </Table>
            </TableContainer>
            </CardContent>
        </Card>
      </Box>
    </Box>
    );
};

export default DocumentsUploadPage;